import type { FileGoverningResourceInput } from "@shuttlerock/api-types/federated";
import { AwsS3Options } from "@uppy/aws-s3";
import { GraphQLClient } from "graphql-request";
import type { CompleteMultipartUploadMutation } from "../graphql/generated/graphql";
import {
  AbortMultipartUpload,
  CompleteMultipartUpload,
  CreateMultipartUpload,
  MultipartUpload,
  SignMultipartUploadPart,
} from "../graphql/queries/multipart-upload.graphql";
import { UploaderFileBody, UploaderFileMeta, UploaderFile } from "../types/UploaderFile";

export type CreateStorageApiUploadOptionsProps = {
  gqlClient: GraphQLClient;
};

export type StorageApiUploadedFile = Omit<
  CompleteMultipartUploadMutation["completeMultipartUpload"],
  "__typename"
>;

export type StorageApiUploadOptions = AwsS3Options<UploaderFileMeta, UploaderFileBody>;

type GoverningResourceInput = Pick<FileGoverningResourceInput, "key" | "kind">;

export function createStorageApiUploadOptions({
  gqlClient,
}: CreateStorageApiUploadOptionsProps): StorageApiUploadOptions {
  const resolveGoverningResource = (file: UploaderFile): GoverningResourceInput => {
    if (!file.meta.governingResource) {
      throw new Error("governingResource metadata is required for storage uploads");
    }

    return file.meta.governingResource;
  };

  return {
    id: "AwsS3",
    shouldUseMultipart: true,
    getChunkSize: () => 64 * 1024 * 1024, // 64 MB

    async abortMultipartUpload(_, { uploadId, signal }) {
      if (!uploadId) return;
      await gqlClient.request({
        document: AbortMultipartUpload,
        variables: { input: { fileKey: uploadId } },
        signal,
      });
    },

    async completeMultipartUpload(_, { uploadId, parts, signal }) {
      const partsInput = parts
        .map(
          (part) =>
            !!part.PartNumber && !!part.ETag && { partNumber: part.PartNumber, eTag: part.ETag },
        )
        .filter(<T>(x: T | false): x is T => !!x);

      if (partsInput.length !== parts.length) {
        throw new Error("Invalid parts");
      }

      const response = await gqlClient.request({
        document: CompleteMultipartUpload,
        variables: {
          input: {
            fileKey: uploadId,
            parts: partsInput,
          },
        },
        signal,
      });

      return {
        file: response.completeMultipartUpload,
        s3Key: response.completeMultipartUpload.s3Key,
        s3Bucket: response.completeMultipartUpload.s3Bucket,
        location: response.completeMultipartUpload.url,
      };
    },

    async createMultipartUpload(file) {
      const governingResource = resolveGoverningResource(file);
      const response = await gqlClient.request(CreateMultipartUpload, {
        input: {
          fileName: file.name,
          fileSize: file.size ?? 0,
          contentType: file.type ?? "",
          metadata: file.meta,
          governingResource,
        },
      });

      return {
        uploadId: response.createMultipartUpload.key,
        key: response.createMultipartUpload.s3Key,
        fileKey: response.createMultipartUpload.key,
      };
    },

    async listParts(_, { uploadId, signal }) {
      if (!uploadId) return [];
      const response = await gqlClient.request({
        document: MultipartUpload,
        variables: { fileKey: uploadId },
        signal,
      });

      return response.multipartUpload.parts.map((x) => ({
        PartNumber: x.partNumber,
        Size: x.size,
        ETag: x.eTag ?? undefined,
      }));
    },

    async signPart(_, { uploadId, partNumber, signal }) {
      const response = await gqlClient.request({
        document: SignMultipartUploadPart,
        variables: {
          params: {
            fileKey: uploadId,
            partNumber,
          },
        },
        signal,
      });

      return {
        url: response.multipartUploadURL.presignedUrl,
        expires: response.multipartUploadURL.expiresIn,
      };
    },
  };
}
